<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

const globalConfig = useMapGetter('globalConfig/get');
const currentUser = useMapGetter('getCurrentUser');
const uiFlags = useMapGetter('inboxes/getUIFlags');

const channelName = ref('');
const isCreating = ref(false);
const createdInboxId = ref(null);
const createdIdentifier = ref('');
const qrImage = ref('');
const connectionStatus = ref('');
let pollTimer = null;

const rules = { channelName: { required } };
const v$ = useVuelidate(rules, { channelName });
const isSubmitDisabled = computed(() => v$.value.$invalid);

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

async function pollQR() {
  const serviceUrl = globalConfig.value.whatsappWebServiceUrl;
  if (!serviceUrl || !createdIdentifier.value) return;

  try {
    const res = await fetch(
      `${serviceUrl}/api/sessions/${createdIdentifier.value}/qr`
    );
    if (res.status === 204) {
      connectionStatus.value = 'connecting';
    } else if (res.ok) {
      const data = await res.json();
      if (data.qr) {
        qrImage.value = data.qr;
        connectionStatus.value = 'qr_ready';
      }
      if (data.status === 'connected') {
        connectionStatus.value = 'connected';
        qrImage.value = '';
        stopPolling();
        return;
      }
    }
  } catch {
    // ignore, will retry
  }

  try {
    const statusRes = await fetch(
      `${globalConfig.value.whatsappWebServiceUrl}/api/sessions/${createdIdentifier.value}/status`
    );
    if (statusRes.ok) {
      const statusData = await statusRes.json();
      if (statusData.status === 'connected') {
        connectionStatus.value = 'connected';
        qrImage.value = '';
        stopPolling();
      }
    }
  } catch {
    // ignore
  }
}

function startPolling() {
  if (pollTimer) return;
  pollQR();
  pollTimer = setInterval(pollQR, 3000);
}

function goToAgents() {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: createdInboxId.value },
  });
}

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  isCreating.value = true;

  try {
    const serviceUrl = globalConfig.value.whatsappWebServiceUrl;
    const internalUrl =
      globalConfig.value.whatsappWebServiceInternalUrl || serviceUrl;
    const accessToken = currentUser.value.access_token;

    const apiChannel = await store.dispatch('inboxes/createChannel', {
      name: channelName.value.trim(),
      channel: {
        type: 'api',
        webhook_url: '',
        additional_attributes: {
          channel_subtype: 'whatsapp_web',
        },
      },
    });

    const identifier = apiChannel.inbox_identifier;
    createdInboxId.value = apiChannel.id;
    createdIdentifier.value = identifier;

    await store.dispatch('inboxes/updateInbox', {
      id: apiChannel.id,
      channel: {
        webhook_url: `${internalUrl}/webhook/${identifier}`,
      },
    });

    await fetch(`${serviceUrl}/api/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: identifier,
        name: channelName.value.trim(),
        chatwootInboxIdentifier: identifier,
        chatwootApiAccessToken: accessToken,
        chatwootAccountId: accountId.value,
      }),
    });

    connectionStatus.value = 'connecting';
    startPolling();
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB_CHANNEL.API.ERROR_MESSAGE'));
  } finally {
    isCreating.value = false;
  }
}

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.WHATSAPP_WEB_CHANNEL.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.WHATSAPP_WEB_CHANNEL.DESC')"
    />

    <form
      v-if="!createdInboxId"
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ t('INBOX_MGMT.ADD.WHATSAPP_WEB_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="
              t('INBOX_MGMT.ADD.WHATSAPP_WEB_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">
            {{ t('INBOX_MGMT.ADD.WHATSAPP_WEB_CHANNEL.CHANNEL_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div>
        <NextButton
          :is-loading="isCreating || uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.WHATSAPP_WEB_CHANNEL.SUBMIT_BUTTON')"
          type="submit"
          solid
          blue
        />
      </div>
    </form>

    <div v-else class="flex flex-col items-center gap-4 mt-4">
      <p
        v-if="connectionStatus === 'connecting'"
        class="text-sm text-n-slate-9"
      >
        {{ $t('INBOX_MGMT.FINISH.WHATSAPP_WEB_CONNECTING') }}
      </p>

      <template v-if="connectionStatus === 'qr_ready' && qrImage">
        <p class="text-sm text-n-slate-9">
          {{ $t('INBOX_MGMT.FINISH.WHATSAPP_WEB_QR_INSTRUCTION') }}
        </p>
        <div class="rounded-lg shadow outline-1 outline-n-strong outline">
          <img
            :src="qrImage"
            alt="WhatsApp QR Code"
            class="rounded-lg size-48 dark:invert"
          />
        </div>
      </template>

      <p
        v-if="connectionStatus === 'connected'"
        class="text-sm font-medium text-green-600"
      >
        {{ $t('INBOX_MGMT.FINISH.WHATSAPP_WEB_CONNECTED') }}
      </p>

      <NextButton
        :label="$t('INBOX_MGMT.ADD.WHATSAPP_WEB_CHANNEL.CONTINUE_BUTTON')"
        solid
        blue
        @click="goToAgents"
      />
    </div>
  </div>
</template>
